/**
 * A region's workshops on a phone: the next two weeks, with a button that
 * reaches a month further each time.
 *
 * Only the first block failing replaces the list with a note. A later block
 * failing leaves what is already loaded on screen, with a retry under it.
 */
import { useMemo } from 'react';

import { toWorkshopChips } from '../../lib/workshop-chips';
import type { CalendarState } from '../../primitives/use-calendar-state';
import { WorkshopsAheadFooter } from '../../primitives/WorkshopsAheadFooter';
import { BucketLoading } from '../../regional/BucketStates';

import { useRegionWorkshopsAhead } from './overview-api';
import { NOTE, useOpenWorkshop } from './region-workshops-shared';
import { RegionWorkshopsPhone } from './RegionWorkshopsPhone';

export interface RegionWorkshopsAheadProps {
  /** The region's full ancestor path. */
  path: string[];
  /** The page's choices; the list follows its hidden sub-regions and cancelled switch. */
  calendar: CalendarState;
}

export function RegionWorkshopsAhead({ path, calendar }: RegionWorkshopsAheadProps) {
  const openWorkshop = useOpenWorkshop();
  const blocks = useRegionWorkshopsAhead(path);
  const chips = useMemo(() => toWorkshopChips(blocks.workshops), [blocks.workshops]);

  if (blocks.isFirstLoadError) {
    return <p style={NOTE}>Workshops could not be loaded. Try again in a moment.</p>;
  }
  if (blocks.isLoading) return <BucketLoading />;

  return (
    <RegionWorkshopsPhone
      workshops={chips}
      through={blocks.through}
      viewedRegionSlug={path[path.length - 1]}
      hidden={calendar.hidden}
      onHiddenChange={calendar.setHidden}
      showCancelled={calendar.showCancelled}
      onToggleCancelled={calendar.toggleCancelled}
      onWorkshopClick={openWorkshop}
      footer={
        <WorkshopsAheadFooter
          through={blocks.showThrough ? blocks.through : null}
          emptyBlock={blocks.emptyBlock}
          isLoadingMore={blocks.isLoadingMore}
          loadMoreFailed={blocks.isLoadMoreError}
          onLoadMore={blocks.loadMore}
        />
      }
    />
  );
}
